// SCR-STAFF-910 — نصب On-prem (US-PLT-041، 042، 091): وضعیت لایسنس نصب‌شده با هشدار انقضا و مهلت، بارگذاری فایل لایسنس
// با پیش‌نمایش اعتبارسنجی پیش از نصب (امضا، شناسه نصب، محدوده نسخه) و اعمال بسته امضاشده داده مرجع (فقط نسخه‌های جدیدتر).
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import {
  unwrap,
  type LicensePreview,
  type LicenseState,
  type LicenseStatus,
  type ReferenceBundleResult,
} from '@phoenix/api-client';
import { useIdempotencyKey, useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  ErrorState,
  faDigits,
  JalaliDate,
  ListPage,
  QueryState,
  StatusChip,
  type StatusTone,
} from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useId, useState } from 'react';
import { pltKeys, toCount, WriteNotice } from './common';

export const SCR_STAFF_910 = 'SCR-STAFF-910';
export const LICENSE_INSTALL_PERMISSION = 'onprem.license.install';
export const REFERENCE_BUNDLE_PERMISSION = 'onprem.reference_data.install';

/** رنگ ثابت وضعیت لایسنس (BR-PLT-051: Active ← Expiring ← Grace ← Expired). */
export function licenseStateTone(state: LicenseState): StatusTone {
  switch (state) {
    case 'Active':
      return 'active';
    case 'Expiring':
      return 'pending';
    case 'Grace':
      return 'no_show';
    case 'Expired':
      return 'error';
    default:
      return 'inactive';
  }
}

/** خواندن متن فایل (UTF-8) با FileReader (سازگار با jsdom). */
function readText(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
    reader.onerror = () => reject(reader.error ?? new Error('read failed'));
    reader.readAsText(file, 'utf-8');
  });
}

function FilePicker({
  label,
  testId,
  onPicked,
}: {
  label: string;
  testId: string;
  onPicked: (name: string, content: string) => void;
}) {
  const inputId = useId();
  return (
    <Box>
      <Button component="label" htmlFor={inputId} variant="outlined" startIcon={<UploadFileOutlinedIcon />}>
        {label}
      </Button>
      <input
        id={inputId}
        type="file"
        accept=".json,application/json"
        hidden
        aria-label={label}
        data-testid={testId}
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) {
            void readText(file).then((content) => onPicked(file.name, content));
          }
        }}
      />
    </Box>
  );
}

function StatusCard({ status }: { status: LicenseStatus }) {
  const { t } = useTranslation('plt');
  const days = toCount(status.daysLeft);
  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid="license-status">
      <Stack spacing={1}>
        <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
          <StatusChip
            tone={licenseStateTone(status.state)}
            labelKey={`plt:installation.state.${status.state.toLowerCase()}`}
          />
          {status.plan && <Chip size="small" variant="outlined" label={status.plan} />}
          <Chip
            size="small"
            variant="outlined"
            color={status.versionInRange ? 'default' : 'error'}
            label={t('plt:installation.version', { value: status.currentVersion })}
          />
        </Stack>
        {status.customerName && <Typography>{status.customerName}</Typography>}
        {status.installationId && (
          <Typography variant="body2" color="text.secondary">
            {t('plt:installation.installation_id')}: <bdi dir="ltr">{status.installationId}</bdi>
          </Typography>
        )}
        {status.validUntil && (
          <Typography variant="body2">
            {t('plt:installation.valid_until')}: <JalaliDate value={status.validUntil} />
            {' · '}
            {days >= 0
              ? t('plt:installation.days_left', { days: faDigits(days) })
              : t('plt:installation.days_past', { days: faDigits(-days) })}
          </Typography>
        )}
        {status.state === 'Grace' && status.graceEndsAt && (
          <Alert severity="warning" data-testid="license-grace">
            <AlertTitle>{t('plt:installation.grace.title')}</AlertTitle>
            {t('plt:installation.grace.body')} <JalaliDate value={status.graceEndsAt} />
          </Alert>
        )}
        {status.state === 'Expired' && (
          <Alert severity="error" data-testid="license-expired">
            {t('plt:installation.expired_read_only')}
          </Alert>
        )}
        {status.state === 'Missing' && (
          <Alert severity="info" data-testid="license-missing">
            {t('plt:installation.missing')}
          </Alert>
        )}
        {status.clockTamperDetectedAt && (
          <Alert severity="error" data-testid="license-clock-tamper">
            {t('plt:installation.clock_tamper')}
          </Alert>
        )}
      </Stack>
    </Paper>
  );
}

function PreviewCard({ preview }: { preview: LicensePreview }) {
  const { t } = useTranslation('plt');
  const valid = preview.verdict === 'Valid';
  return (
    <Alert
      severity={valid ? 'success' : 'error'}
      data-testid="license-preview"
      data-verdict={preview.verdict}
    >
      <AlertTitle>{t(`plt:installation.verdict.${preview.verdict.toLowerCase()}`)}</AlertTitle>
      <Stack spacing={0.5}>
        {preview.customerName && <Typography variant="body2">{preview.customerName}</Typography>}
        {preview.plan && (
          <Typography variant="body2">
            {t('plt:installation.plan')}: {preview.plan}
          </Typography>
        )}
        {preview.validUntil && (
          <Typography variant="body2">
            {t('plt:installation.valid_until')}: <JalaliDate value={preview.validUntil} />
          </Typography>
        )}
        {preview.verdict === 'InstallationMismatch' && (
          <Typography variant="body2">
            {t('plt:installation.expected_installation')}:{' '}
            <bdi dir="ltr">{preview.expectedInstallationId}</bdi>
          </Typography>
        )}
        {preview.suggestedVersion && (
          <Typography variant="body2" data-testid="license-suggested-version">
            {t('plt:installation.suggested_version', { value: preview.suggestedVersion })}
          </Typography>
        )}
        {preview.features.length > 0 && (
          <Typography variant="body2" color="text.secondary">
            {t('plt:installation.features', { count: faDigits(preview.features.length) })}
          </Typography>
        )}
      </Stack>
    </Alert>
  );
}

function BundleResult({ result }: { result: ReferenceBundleResult }) {
  const { t } = useTranslation('plt');
  return (
    <Alert
      severity={result.applied.length > 0 ? 'success' : 'info'}
      data-testid="bundle-result"
      data-applied={faDigits(result.applied.length)}
    >
      <AlertTitle>
        {result.applied.length > 0
          ? t('plt:installation.bundle.applied', { count: faDigits(result.applied.length) })
          : t('plt:installation.bundle.nothing_newer')}
      </AlertTitle>
      {result.applied.length > 0 && (
        <Typography variant="body2">
          <bdi dir="ltr">{result.applied.join(', ')}</bdi>
        </Typography>
      )}
      {result.skipped.length > 0 && (
        <Typography variant="body2" color="text.secondary">
          {t('plt:installation.bundle.skipped', { count: faDigits(result.skipped.length) })}
        </Typography>
      )}
      {result.unknownDatasets.length > 0 && (
        <Typography variant="body2" color="text.secondary">
          {t('plt:installation.bundle.unknown', { value: result.unknownDatasets.join(', ') })}
        </Typography>
      )}
    </Alert>
  );
}

export function InstallationPage() {
  const { t } = useTranslation('plt');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const write = useWriteAccess(LICENSE_INSTALL_PERMISSION);
  const bundleWrite = useWriteAccess(REFERENCE_BUNDLE_PERMISSION);
  const idempotency = useIdempotencyKey();
  const bundleIdempotency = useIdempotencyKey();
  const [license, setLicense] = useState<{ name: string; content: string } | null>(null);

  const status = useQuery({
    queryKey: pltKeys.license(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/platform/license')),
  });

  const preview = useMutation({
    mutationFn: (file: { name: string; content: string }) =>
      unwrap(
        tenantApi.POST('/api/v1/platform/license:preview', {
          body: { content: file.content, fileName: file.name },
        }),
      ),
  });

  const install = useMutation({
    mutationFn: (file: { name: string; content: string }) =>
      unwrap(
        tenantApi.POST('/api/v1/platform/license:install', {
          body: { content: file.content, fileName: file.name },
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: async () => {
      setLicense(null);
      preview.reset();
      await queryClient.invalidateQueries({ queryKey: pltKeys.license(session?.tenantId) });
    },
  });

  const bundle = useMutation({
    mutationFn: (file: { name: string; content: string }) =>
      unwrap(
        tenantApi.POST('/api/v1/platform/reference-data/bundle:import', {
          body: { content: file.content, fileName: file.name },
          headers: { 'Idempotency-Key': bundleIdempotency.current() },
        }),
      ),
    onSettled: (_data, error) => bundleIdempotency.settle(error ?? undefined),
  });

  return (
    <ListPage screenId={SCR_STAFF_910} titleKey="plt:installation.title">
      <Stack spacing={2}>
        <WriteNotice write={write} />
        <QueryState
          isPending={status.isPending}
          error={status.error}
          data={status.data}
          isEmpty={() => false}
          onRetry={() => void status.refetch()}
        >
          {(data) => (
            <>
              <StatusCard status={data} />
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={1.5}>
                  <Typography variant="subtitle1">{t('plt:installation.upload.title')}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('plt:installation.upload.help')}
                  </Typography>
                  <FilePicker
                    label={t('plt:installation.upload.choose')}
                    testId="license-file-input"
                    onPicked={(name, content) => {
                      install.reset();
                      setLicense({ name, content });
                      preview.mutate({ name, content });
                    }}
                  />
                  {license && (
                    <Typography variant="body2">
                      <bdi dir="ltr">{license.name}</bdi>
                    </Typography>
                  )}
                  {preview.error ? <ErrorState error={preview.error} compact /> : null}
                  {preview.data ? <PreviewCard preview={preview.data} /> : null}
                  {install.error ? <ErrorState error={install.error} compact /> : null}
                  <Box>
                    <Button
                      variant="contained"
                      data-testid="license-install"
                      disabled={
                        !write.canWrite ||
                        !license ||
                        install.isPending ||
                        preview.isPending ||
                        preview.data?.verdict !== 'Valid'
                      }
                      onClick={() => license && install.mutate(license)}
                    >
                      {t('plt:installation.upload.install')}
                    </Button>
                  </Box>
                </Stack>
              </Paper>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={1.5}>
                  <Typography variant="subtitle1">{t('plt:installation.bundle.title')}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('plt:installation.bundle.help')}
                  </Typography>
                  <FilePicker
                    label={t('plt:installation.bundle.choose')}
                    testId="bundle-file-input"
                    onPicked={(name, content) => {
                      if (bundleWrite.canWrite) {
                        bundle.mutate({ name, content });
                      }
                    }}
                  />
                  {bundle.error ? <ErrorState error={bundle.error} compact /> : null}
                  {bundle.data ? <BundleResult result={bundle.data} /> : null}
                </Stack>
              </Paper>
            </>
          )}
        </QueryState>
      </Stack>
    </ListPage>
  );
}
